

import React, { createContext, useContext, useCallback, useState } from "react";
import confetti from "canvas-confetti";
import { AchievementToast } from "./achievement-toast";

export interface CelebrationEvent {
  id: string;
  type: "achievement" | "level_up" | "points" | "personal_best" | "skill_unlocked" | "skill_mastered";
  title: string;
  description?: string;
  icon?: string;
  points?: number;
}

interface CelebrationContextType {
  celebrate: (event: CelebrationEvent) => void;
  celebrateMultiple: (events: CelebrationEvent[]) => void;
  fireConfetti: (type?: "default" | "gold" | "stars") => void;
}

const CelebrationContext = createContext<CelebrationContextType | undefined>(undefined);

export function useCelebration() {
  const context = useContext(CelebrationContext);
  if (!context) {
    throw new Error("useCelebration must be used within CelebrationProvider");
  }
  return context;
}

export function CelebrationProvider({ children }: { children: React.ReactNode }) {
  const [activeToasts, setActiveToasts] = useState<CelebrationEvent[]>([]);

  const fireConfetti = useCallback((type: "default" | "gold" | "stars" = "default") => {
    const defaults = {
      spread: 360,
      ticks: 100,
      gravity: 0.5,
      decay: 0.94,
      startVelocity: 30,
      origin: { y: 0.6 },
    };

    switch (type) {
      case "gold":
        // Gold confetti for achievements
        confetti({
          ...defaults,
          particleCount: 100,
          colors: ["#FFD700", "#FFA500", "#FF8C00", "#FFB347"],
          shapes: ["circle", "square"],
        });
        setTimeout(() => {
          confetti({
            ...defaults,
            particleCount: 50,
            colors: ["#FFD700", "#FFA500"],
            origin: { x: 0.2, y: 0.6 },
          });
        }, 150);
        setTimeout(() => {
          confetti({
            ...defaults,
            particleCount: 50,
            colors: ["#FFD700", "#FFA500"],
            origin: { x: 0.8, y: 0.6 },
          });
        }, 300);
        break;

      case "stars":
        // Stars for level up
        const shapes = confetti.shapeFromText({ text: "⭐", scalar: 2 });
        confetti({
          ...defaults,
          particleCount: 30,
          shapes: [shapes],
          scalar: 2,
        });
        confetti({
          ...defaults,
          particleCount: 100,
          colors: ["#9333ea", "#a855f7", "#c084fc", "#e879f9"],
        });
        break;

      default:
        // Colorful confetti
        confetti({
          ...defaults,
          particleCount: 100,
          colors: ["#26ccff", "#a25afd", "#ff5e7e", "#88ff5a", "#fcff42", "#ffa62d"],
        });
    }
  }, []);

  const celebrate = useCallback((event: CelebrationEvent) => {
    setActiveToasts((prev) => [...prev, event]);

    // Fire confetti based on event type
    if (event.type === "achievement") {
      fireConfetti("gold");
    } else if (event.type === "level_up") {
      fireConfetti("stars");
    } else if (event.type === "skill_mastered") {
      fireConfetti("gold");
    } else if (event.type === "personal_best" || event.type === "skill_unlocked") {
      fireConfetti("default");
    }

    // Remove toast after delay
    setTimeout(() => {
      setActiveToasts((prev) => prev.filter((t) => t.id !== event.id));
    }, 5000);
  }, [fireConfetti]);

  const celebrateMultiple = useCallback((events: CelebrationEvent[]) => {
    events.forEach((event, index) => {
      setTimeout(() => {
        celebrate(event);
      }, index * 800); // Stagger celebrations
    });
  }, [celebrate]);

  const dismissToast = useCallback((id: string) => {
    setActiveToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <CelebrationContext.Provider value={{ celebrate, celebrateMultiple, fireConfetti }}>
      {children}
      {/* Toast container */}
      <div className="fixed top-4 right-4 z-50 flex flex-col gap-3 pointer-events-none">
        {activeToasts.map((toast, index) => (
          <AchievementToast
            key={toast.id}
            event={toast}
            onDismiss={() => dismissToast(toast.id)}
            style={{ animationDelay: `${index * 100}ms` }}
          />
        ))}
      </div>
    </CelebrationContext.Provider>
  );
}
